import { todayKey } from '@/lib/day'

// One-time, per-device day-1 flags set when onboarding finishes.

const START_HERE_KEY = 'goaltree-start-here'
const BRAIN_DUMP_TIP_KEY = 'goaltree-brain-dump-tip'
const BRAIN_DUMP_USED_KEY = 'goaltree-brain-dump-used'
const SOFT_UPSELL_KEY = 'goaltree-soft-upsell-shown'
const FLAME_TIP_KEY = 'goaltree-flame-tip-seen'
const ONBOARDING_PAYWALL_KEY = 'goaltree-onboarding-paywall'
const NEW_GOAL_SEEN_KEY = 'goaltree-new-goal-seen'

function read(key: string): string | null {
  try {
    return localStorage.getItem(key)
  } catch {
    return null
  }
}

function write(key: string, value: string | null) {
  try {
    if (value === null) localStorage.removeItem(key)
    else localStorage.setItem(key, value)
  } catch {}
}

// Stored as "goalId@day": the highlight only lasts the day it was set.
export function startHereGoalId(): string | null {
  const [goalId, day] = (read(START_HERE_KEY) ?? '').split('@')
  return goalId && day === todayKey() ? goalId : null
}
export const clearStartHere = () => write(START_HERE_KEY, null)
export const markStartHere = (goalId: string) => write(START_HERE_KEY, `${goalId}@${todayKey()}`)

// The "You're set" banner on a just-created goal's page shows once per goal.
export const newGoalBannerPending = (goalId: string) =>
  startHereGoalId() === goalId && read(NEW_GOAL_SEEN_KEY) !== goalId
export const dismissNewGoalBanner = (goalId: string) => write(NEW_GOAL_SEEN_KEY, goalId)

export function markOnboardingFinished(goalId: string | null) {
  if (goalId) markStartHere(goalId)
  write(BRAIN_DUMP_TIP_KEY, 'pending')
  write(ONBOARDING_PAYWALL_KEY, 'pending')
}

export const onboardingPaywallPending = () => read(ONBOARDING_PAYWALL_KEY) === 'pending'
export const markOnboardingPaywallShown = () => write(ONBOARDING_PAYWALL_KEY, 'seen')

// 'pending' (day 1) → 'seen' → 'again' (second chance, a week in, if Brain dump is
// still unused) → 'done'.
const SECOND_TIP_AFTER_MS = 7 * 24 * 60 * 60 * 1000

export const brainDumpTipPending = () => {
  const v = read(BRAIN_DUMP_TIP_KEY)
  return v === 'pending' || v === 'again'
}
export const dismissBrainDumpTip = () => {
  const v = read(BRAIN_DUMP_TIP_KEY)
  if (v === 'pending') write(BRAIN_DUMP_TIP_KEY, 'seen')
  else if (v === 'again') write(BRAIN_DUMP_TIP_KEY, 'done')
}

export const brainDumpUsed = () => read(BRAIN_DUMP_USED_KEY) === '1'
export const markBrainDumpUsed = () => write(BRAIN_DUMP_USED_KEY, '1')

export const secondBrainDumpTipDue = (onboardedAt: number | undefined) => {
  const v = read(BRAIN_DUMP_TIP_KEY)
  return (
    !!onboardedAt &&
    v !== 'pending' &&
    v !== 'again' &&
    v !== 'done' &&
    !brainDumpUsed() &&
    Date.now() - onboardedAt >= SECOND_TIP_AFTER_MS
  )
}
export const armSecondBrainDumpTip = () => write(BRAIN_DUMP_TIP_KEY, 'again')

export const softUpsellShown = () => read(SOFT_UPSELL_KEY) === '1'
export const markSoftUpsellShown = () => write(SOFT_UPSELL_KEY, '1')

export const flameTipSeen = () => read(FLAME_TIP_KEY) === '1'
export const markFlameTipSeen = () => write(FLAME_TIP_KEY, '1')
